import { useIsDashboardEditable } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { alpha, Box } from '@mui/material';
import { DashboardCell, DashboardWidget } from 'generated/graphql';
import { useState } from 'react';
import { AddMediaButton } from './AddMediaButton';
import { DashboardImagePreview } from './DashboardImagePreview';
import { MediaWidgetActions } from './MediaWidgetActions';

interface DashboardMediaWidgetProps {
  rowID: string;
  cell: DashboardCell;
  imageWidget?: DashboardWidget['imageWidget'];
  videoWidget?: DashboardWidget['videoWidget'];
}

export default function DashboardMediaWidget({
  cell,
  imageWidget,
  videoWidget,
  rowID,
}: DashboardMediaWidgetProps) {
  const isEditable = useIsDashboardEditable();
  const [isEditorActive, setIsEditorActive] = useState(false);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const isUpdateDashboardInProgress = useDashboardStore(
    ({ visual }) => visual.isUpdateDashboardInProgress,
  );

  if (!dashboard) {
    return null;
  }

  const renderContent = () => {
    if (!imageWidget?.url && !videoWidget?.url) {
      return <AddMediaButton rowID={rowID} cell={cell} />;
    }

    if (videoWidget?.url) {
      return (
        <Box
          component="iframe"
          width="100%"
          height="100%"
          src={videoWidget?.url}
          title="cell.content_id"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            backgroundColor: '#000',
            border: 0,
            borderRadius: 1.5,
            colorScheme: 'normal',
            padding: '0 39px',
          }}
        />
      );
    }

    if (imageWidget?.url) {
      return <DashboardImagePreview src={imageWidget?.url} />;
    }

    return null;
  };

  return (
    <Box
      className="et-media-widget"
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 1.5,
        position: 'relative',
        ':hover': isEditable
          ? {
              '.media-content-actions': {
                visibility: 'visible',
              },
              backgroundColor: isEditorActive
                ? (theme) => alpha(theme.palette.background.paper, 0.8)
                : undefined,
            }
          : undefined,
      }}
      onMouseOver={() => {
        setIsEditorActive(true);
      }}
      onMouseLeave={() => {
        setIsEditorActive(false);
      }}
    >
      {isUpdateDashboardInProgress ? null : (
        <MediaWidgetActions
          isEditorActive={isEditorActive}
          sx={{
            position: 'absolute',
            top: 8,
            right: 4,
            background: (theme) => theme.palette.background.paper,
            borderRadius: '50%',
          }}
          className="media-content-actions"
          cell={cell}
          rowID={rowID}
          imageLink={imageWidget?.url}
          videoLink={videoWidget?.url}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          height: '100%',
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
}
